import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { TableContext } from '@eas/common-web';
import { useListenToWorkOrderChanges } from '@composite/websocket/listen-to-work-order-changes';
import { WebsocketCacheControlContext } from '@composite/websocket/websocket-cache-control-context';

export const ClearWorkOrderTableCacheContext = createContext<{
  clearCache?: () => void;
  registerListener?: (listener: (count: number) => void) => () => void;
}>({});

export const useTableWebsocketUtils = () => {
  const listenersRef = useRef<((count: number) => void)[]>([]);

  const registerListener = useCallback((listener: (count: number) => void) => {
    listenersRef.current.push(listener);
    return () => {
      listenersRef.current = listenersRef.current.filter(
        (l: (count: number) => void) => l !== listener
      );
    };
  }, []);

  const notifyListeners = useCallback((count: number) => {
    listenersRef.current.forEach((listener) => listener(count));
  }, []);

  const handleCacheChange = useCallback(
    (cache: Record<string, any>) => {
      notifyListeners(Object.keys(cache).length);
    },
    [notifyListeners]
  );

  const { resendAllChangedWorkOrders, clearCache } =
    useListenToWorkOrderChanges({
      getRowId: (workOrder) => workOrder?.id ?? '',
      variant: 'table',
      cacheContentObserver: handleCacheChange,
    });

  const wsCache = useContext(WebsocketCacheControlContext);

  useImperativeHandle(wsCache['table'], () => clearCache);

  /**
   * Utility component to clear websocket cache on sorts and filters change
   */
  const AutoClearCacheOnStateChange = useMemo(
    () =>
      function AutoClearCacheOnStateChange() {
        const { sorts, filtersState } = useContext(TableContext);
        useEffect(() => {
          clearCache?.();
        }, [sorts, filtersState]);

        return null;
      },
    [clearCache]
  );

  return {
    resendAllChangedWorkOrders,
    clearCache,
    registerListener,
    AutoClearCacheOnStateChange,
  };
};
